import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { SessionFilter, getBotDeploymentIdsByBotIdSelector } from '@common/reducers/ChatbotSelectors';
import { AutomationSessionReducerActions, DBAutomationSessionThunks, automationActionsActions, automationSessionSelectByQuery } from '@common/reducers/DBServiceThunks';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { SessionFilters } from './SessionFilters';
import { SessionRow } from './SessionRow';

export interface SessionsListProps {
    filter: SessionFilter;
    onSessionFilterChange: (filter: SessionFilter) => void;
    selectedDeploymentId: string | null;
    onDeploymentSelect: (deploymentId: string | null) => void;
}

const EmptyState = ({ message }: { message: string }) => (
    <div className="flex flex-col items-center justify-center h-full w-full flex-1 text-center p-8">
        <h3 className="text-base font-semibold mb-2">
            No Sessions Found
        </h3>
        <p className="text-sm text-gray-500">
            {message}
        </p>
    </div>
);

// Pagination settings
const SESSIONS_PER_PAGE = 25;

export const SessionsList = ({ 
    filter, 
    onSessionFilterChange, 
    selectedDeploymentId, 
    onDeploymentSelect
}: SessionsListProps) => {
    const dispatch = useAppDispatch();
    const { botId } = useParams<{ botId: string }>();
    
    // Create a ref for the intersection observer target
    const observerTarget = useRef<HTMLDivElement>(null);

    // Get loading state and pagination info from Redux
    const { 
        itemsLoading: isLoading, 
        itemsLoaded,
        total: totalSessions,
        skip,
        limit 
    } = useAppSelector(state => state.DBAutomationSessionReducer);
    
    // Calculate current page from skip and limit
    const currentPage = Math.floor(skip / limit) + 1;
    const totalPages = Math.ceil(totalSessions / limit);
    
    const deploymentIds = useAppSelector(state => 
        botId ? getBotDeploymentIdsByBotIdSelector(state, botId) : []
    );

    // Get sessions directly from the store using the selector
    const sessions = useAppSelector(state => {
        const query: any = {};
        
        // Apply filter based on status if needed
        if (filter === 'active') {
            query.status = { $ne: 'ENDED' };
        } else if (filter === 'inactive') {
            query.status = 'ENDED';
        }
        
        // Apply filter for botId
        if (botId) {
            query.botId = botId;
        }
        
        // Apply filter for deploymentId if selected
        if (selectedDeploymentId) {
            query.deploymentId = selectedDeploymentId;
        }
        
        return automationSessionSelectByQuery(state.DBAutomationSessionReducer, query);
    });
    
    console.log('Redux pagination status:', { 
        currentPage, 
        totalPages, 
        totalSessions, 
        skip, 
        limit,
        isLoading,
        itemsLoaded,
        sessionsCount: sessions.length
    });

    // Function to fetch sessions with the correct query
    const fetchSessions = useCallback((page: number, isInfiniteScroll = false) => {
        if (!botId) return;
        
        // Clear existing sessions if not infinite scrolling
        if (!isInfiniteScroll || page === 1) {
            dispatch(AutomationSessionReducerActions.clearState());
        }
        
        // Set up query parameters
        const query: any = {
            botId: botId,
            $limit: SESSIONS_PER_PAGE,
            $skip: (page - 1) * SESSIONS_PER_PAGE,
            $sort: { updatedAt: -1 } // Most recent first
        };
        
        // Apply filter based on status if needed
        if (filter === 'active') {
            query.status = { $ne: 'ENDED' };
        } else if (filter === 'inactive') {
            query.status = 'ENDED';
        }
        
        // Apply filter for deploymentId if selected
        if (selectedDeploymentId) {
            query.deploymentId = selectedDeploymentId;
        }
        
        console.log('Fetching sessions with query:', query);

        dispatch(AutomationSessionReducerActions.clearState());
        
        dispatch(DBAutomationSessionThunks.find(query))
            .unwrap()
            .then(result => {
                console.log('Sessions fetch result:', result);
            })
            .catch(error => {
                console.error('Error fetching sessions:', error);
            });
    }, [botId, filter, selectedDeploymentId, dispatch]);

    // Initial fetch and refetch when dependencies change
    useEffect(() => {
        console.log('Filter or deployment changed, fetching sessions', {
            botId,
            filter,
            selectedDeploymentId
        });
        fetchSessions(1);
    }, [botId, filter, selectedDeploymentId, fetchSessions]);
    
    // Handle page changes
    const handlePageChange = (newPage: number) => {
        const isInfiniteScroll = newPage > currentPage;
        fetchSessions(newPage, isInfiniteScroll);
    };
    
    // Handlers for pagination buttons
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            handlePageChange(currentPage + 1);
        }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pb-3 gap-4 border-b border-gray-200 flex flex-row flex-start items-center">
                <h5 className="text-base font-semibold">
                    Sessions
                </h5>
                <div>
                    <SessionFilters 
                        sessionFilter={filter} 
                        onSessionFilterChange={onSessionFilterChange}
                        selectedDeploymentId={selectedDeploymentId}
                        onDeploymentSelect={onDeploymentSelect}
                    />
                </div>
            </div>
            
            {/* Content area with fixed height and scrollable */}
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                {/* Scrollable sessions area */}
                <div className="flex-1 overflow-auto">
                    {isLoading && sessions.length === 0 ? (
                        <div className="p-3 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <div className="flex-1">
                                    <div className="w-2/5 h-6 bg-gray-100 rounded mb-1" />
                                    <div className="w-3/5 h-5 bg-gray-100 rounded" />
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-24 h-5 bg-gray-100 rounded" />
                                    <div className="w-24 h-5 bg-gray-100 rounded" />
                                    <div className="w-20 h-6 bg-gray-100 rounded" />
                                </div>
                            </div>
                        </div>
                    ) : sessions.length === 0 && itemsLoaded ? (
                        <EmptyState 
                            message={`There are no ${filter === 'all' ? '' : `${filter} `}sessions ${selectedDeploymentId ? 'for this version' : ''}`}
                        />
                    ) : (
                        <>
                            {/* Sessions list */}
                            {sessions.map(session => (
                                <SessionRow key={session._id} sessionId={session._id} />
                            ))}
                            
                            {/* Intersection observer target for infinite scroll */}
                            <div ref={observerTarget} className="h-4 w-full" />
                            
                            {/* Loading indicator for pagination */}
                            {isLoading && sessions.length > 0 && (
                                <div className="p-4 flex justify-center">
                                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                </div>
                            )}
                        </>
                    )}
                </div>
                
                {/* Pagination controls - fixed position at the bottom */}
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-white">
                    <div className="text-sm text-gray-500">
                        {totalSessions > 0 ? (
                            `Showing ${Math.min((currentPage - 1) * SESSIONS_PER_PAGE + 1, totalSessions)} to ${Math.min(currentPage * SESSIONS_PER_PAGE, totalSessions)} of ${totalSessions} sessions`
                        ) : (
                            `No sessions found (Redux total: ${totalSessions})`
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={goToPreviousPage} 
                            disabled={currentPage <= 1 || isLoading || totalSessions === 0}
                            className="h-8 w-8 p-0"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <div className="text-sm">
                            Page {currentPage} of {totalPages || 1}
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={goToNextPage} 
                            disabled={currentPage >= totalPages || isLoading || totalSessions === 0}
                            className="h-8 w-8 p-0"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}; 